import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Modal,
  Chip,
  Autocomplete,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { joiEmailSchema } from "../../../../utils/utils";
import { shareInvoice } from "../../../../api/admin/order-management";
import "./invoice-share.scss";
import { AlertComponent } from "../../../../common/components";
import InputAdornment from "@mui/material/InputAdornment";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

interface SharePartnerPopupProps {
  open: boolean;
  onClose: () => void;
  partnerTitle: string;
  onSendEmail?: (email: string) => void;
  shareLink?: string;
  isPlanner?: boolean;
  providerId: number;
  orderId: string;
  customerName: string;
}

const ShareEmailPopup: React.FC<SharePartnerPopupProps> = ({
  open,
  onClose,
  onSendEmail,
  id,
  orderId,
  customerName,
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [successfulMsg, setSuccessfulMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [emailError, setEmailError] = useState("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");

  const validateEmail = (value: string) => {
    const { error } = joiEmailSchema.validate(value);
    return !error;
  };

  const handleSend = async () => {
    if (!emails.length) return;

    setLoading(true);

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await shareInvoice(id, { timezone, emails });

      if (response.data?.success) {
        setSuccessfulMsg("Mail shared successfully");
        setAlertMessage("Invoice shared successfully");
        setAlertType("success");
        setAlertOpen(true);
        setEmails([]);
        setInputValue("");

        setTimeout(() => {
          setSuccessfulMsg("");
        }, 3000);
      }
    } catch (error) {
      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to share invoice. Please try again.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setEmails([]);
      setInvalidEmails([]);
      setEmailError("");
      setSuccessfulMsg("");
      setInputValue("");
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
    >
      <>
        <Box className="share-partner-popup">
          <Box className="share-partner-popup__header">
            <Typography className="share-partner-popup__title">
              Share Invoice
            </Typography>
            <IconButton
              className="share-partner-popup__close-btn"
              onClick={onClose}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
          </Box>

          <div className="share-partner-popup__divider" />
          <Box className="share-partner-popup__section">
            <Typography className="share-partner-popup__label">
              Order ID & Name
            </Typography>

            <Box className="share-partner-popup__readonly-input">
              {orderId} - {customerName}
            </Box>
          </Box>
          {successfulMsg && (
            <Typography className="share-partner-popup__success-msg">
              {successfulMsg}
            </Typography>
          )}
          <Box className="share-partner-popup__section">
            <Typography className="share-partner-popup__label">
              Add People
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={emails}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                onChange={(event, newValue) => {
                  const invalids = newValue.filter(
                    (email) => !validateEmail(email),
                  );

                  setEmails(newValue);
                  setInvalidEmails(invalids);

                  if (invalids.length) {
                    setEmailError(
                      "One or more email addresses are invalid. Please correct them before Sharing.",
                    );
                  } else {
                    setEmailError("");
                  }
                }}
                renderTags={(value: readonly string[], getTagProps) =>
                  value.map((option, index) => {
                    return (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        sx={{
                          color: "#1a73e8",
                          backgroundColor: "#e8f0fe",
                          "& .MuiChip-deleteIcon": {
                            color: "#1a73e8",
                            opacity: 0.9,
                          },
                        }}
                      />
                    );
                  })
                }
                filterOptions={(x) => x}
                popupIcon={null}
                disableCloseOnSelect
                PopperComponent={(props) => <></>}
                renderInput={(params) => (
                  <Box>
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <MailOutlineIcon
                                sx={{
                                  color: "#6b7280",
                                  fontSize: 20,
                                  ml: "6px",
                                }}
                              />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      variant="outlined"
                      placeholder={
                        params.InputProps.startAdornment
                          ? ""
                          : "Enter an email address and press Enter to add it."
                      }
                      size="small"
                      error={Boolean(emailError)}
                      className="share-partner-popup-input"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          minHeight: "50px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderRadius: "10px",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                          borderColor: "#ccc !important",
                        },
                      }}
                    />
                    {emailError && (
                      <Typography className="share-partner-popup__error">
                        {emailError}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Box>
          </Box>
          <Box className="share-partner-popup__share-wrapper">
            <Button
              className="share-partner-popup__cancel-btn"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={!emails.length || invalidEmails.length > 0}
              className="share-partner-popup__share-btn"
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Share Invoice"
              )}
            </Button>
          </Box>
        </Box>
        <AlertComponent
          open={alertOpen}
          message={alertMessage || ""}
          type={alertType}
          onClose={() => setAlertOpen(false)}
        />
      </>
    </Modal>
  );
};

export default ShareEmailPopup;
